/* sugar:assets/popup.js@2.1.7 */
/*
  Shared dialog engine for every popup in the theme.

  One dialog element carries data-popup-dialog="<id>". Anything that opens it
  carries data-popup-open="<id>". Three blocks build that pairing differently:

    Popup        writes data-popup-open on its own trigger button.
    Media Popup  walks back to the preceding sibling matching
                 data-popup-prev="<selector>" and stamps it.
    Text Popup   binds every element matching data-popup-within="<selector>"
                 inside its origin, or inside the nearest ancestor matching
                 data-popup-within-root when that is set. With "the whole
                 block" as the trigger it uses the prev wiring instead, and
                 with "image corner" it builds a small button inside every
                 element matching data-popup-mark="<selector>" in that scope.

  The dialog is moved to <body> on first sight so no ancestor's transform,
  overflow or z-index can trap it. Its origin element is remembered first,
  because the two selector-based wirings have to search where it came from.

  A trigger gets a listener of its own, but data-popup-open is also honoured
  through one delegated handler, so a trigger that a component copies into
  place (the product galleries clone their badges onto the slides) still opens
  its dialog — cloneNode carries attributes, never listeners. Hosts that
  rewrite their text at runtime (perks refill from a template on every offer
  change) drop the bound words entirely, so a within-wired dialog watches its
  scope and binds whatever fresh words appear.

  Display. data-popup-display / data-popup-display-m name the window's shape on
  each breakpoint: center, drawer and sheet are modal (overlay, scroll lock,
  focus trap). tooltip is not — it is anchored beside the trigger by this file,
  opens on hover where hover exists, and closes when the pointer leaves or the
  visitor clicks elsewhere. A click pins a hover-opened tooltip; a second click,
  Escape, or scrolling on past a nudge closes it.
*/
(function () {
  'use strict';

  if (window.__swOk === false) return;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable]'
  ].join(',');

  var TYPEABLE = /^(INPUT|TEXTAREA|SELECT)$/;
  var GAP = 10; // space between a tooltip and its trigger
  var EDGE = 8; // space a tooltip keeps from the viewport edge
  var HOVER_IN = 120;
  var HOVER_OUT = 200;
  var SCROLL_AWAY = 40; // page scroll (px) after which a pinned tooltip lets go

  var mobileQuery = window.matchMedia('(max-width: 749px)');
  var hoverQuery = window.matchMedia('(hover: hover)');

  var openStack = [];

  function modeOf(dialog) {
    var d = dialog.dataset;
    return (mobileQuery.matches ? d.popupDisplayM : d.popupDisplay) || 'center';
  }

  function isTooltip(dialog) {
    return modeOf(dialog) === 'tooltip';
  }

  function anyModalOpen() {
    return openStack.some(function (d) {
      return !isTooltip(d);
    });
  }

  function lockScroll() {
    if (anyModalOpen()) document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (anyModalOpen()) return;
    document.body.style.overflow = '';
  }

  function focusables(dialog) {
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
    });
  }

  function triggersFor(id) {
    return id ? document.querySelectorAll('[data-popup-open="' + id + '"]') : [];
  }

  function setExpanded(dialog, value) {
    Array.prototype.forEach.call(triggersFor(dialog.dataset.popupDialog), function (el) {
      el.setAttribute('aria-expanded', value);
    });
    (dialog.__triggers || []).forEach(function (el) {
      el.setAttribute('aria-expanded', value);
    });
  }

  /* ---- tooltip placement ------------------------------------------------ */

  function anchorFor(dialog, hint) {
    if (hint && document.contains(hint)) return hint;
    var list = dialog.__triggers || [];
    for (var i = 0; i < list.length; i++) {
      if (document.contains(list[i]) && list[i].getClientRects().length) return list[i];
    }
    var any = triggersFor(dialog.dataset.popupDialog);
    return any.length ? any[0] : null;
  }

  function place(dialog) {
    var anchor = dialog.__anchor;
    if (!anchor || !document.contains(anchor)) return;
    var rects = anchor.getClientRects();
    // A trigger that wraps onto two lines has two boxes; aim at the last one so
    // the bubble sits under the end of the phrase, next to a trailing icon.
    var r = rects.length ? rects[rects.length - 1] : anchor.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var w = dialog.offsetWidth;
    var h = dialog.offsetHeight;

    var left = r.left + r.width / 2 - w / 2;
    left = Math.max(EDGE, Math.min(left, vw - w - EDGE));

    var below = r.bottom + GAP;
    var above = r.top - GAP - h;
    var useAbove = below + h > vh - EDGE && above >= EDGE;
    var top = useAbove ? above : below;

    var arrow = r.left + r.width / 2 - left;
    arrow = Math.max(16, Math.min(arrow, w - 16));

    dialog.style.setProperty('--pd-x', Math.round(left) + 'px');
    dialog.style.setProperty('--pd-y', Math.round(top) + 'px');
    dialog.style.setProperty('--pd-arrow-x', Math.round(arrow) + 'px');
    dialog.classList.toggle('is-above', useAbove);
  }

  // A pinned bubble follows its words for a nudge of scrolling, then closes:
  // a visitor who has scrolled on has moved on, and a bubble that trails them
  // down the page reads as nagging. The hover-opened kind already closes on
  // its own when the pointer leaves.
  function placeOpenTooltips() {
    openStack.slice().forEach(function (d) {
      if (!isTooltip(d)) return;
      if (d.dataset.popupHold !== 'true' && Math.abs(window.scrollY - (d.__scrollY || 0)) > SCROLL_AWAY) {
        close(d);
        return;
      }
      place(d);
    });
  }

  var placing = false;
  function onViewportChange() {
    if (placing) return;
    placing = true;
    requestAnimationFrame(function () {
      placing = false;
      placeOpenTooltips();
    });
  }

  /* ---- open / close ------------------------------------------------------ */

  function open(dialog, opts) {
    opts = opts || {};
    cancelClose(dialog);
    if (dialog.classList.contains('is-open')) return;
    // In the theme editor the dialog stays where its block rendered: the editor
    // finds a block's markup inside its section, so a dialog moved to <body>
    // cannot be clicked into or outlined there.
    var designMode = window.Shopify && window.Shopify.designMode;
    if (!designMode && dialog.parentElement !== document.body) document.body.appendChild(dialog);
    // Left in place, the dialog can only stack as high as its section, and a
    // sticky header section sits above the rest of the page. Lift the section
    // while the dialog is open so the window covers the header there too.
    var host = designMode && dialog.closest('.shopify-section');
    if (host) host.classList.add('sugar-popup-host-open');
    // Images arrive without a source (see popup-dialog.liquid); they get it now.
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-pd-src], [data-pd-srcset]'), function (el) {
      if (el.dataset.pdSrcset) { el.setAttribute('srcset', el.dataset.pdSrcset); el.removeAttribute('data-pd-srcset'); }
      if (el.dataset.pdSrc) { el.setAttribute('src', el.dataset.pdSrc); el.removeAttribute('data-pd-src'); }
    });

    var tooltip = isTooltip(dialog);
    // One bubble at a time: opening a tooltip puts any other away.
    if (tooltip) {
      openStack.slice().forEach(function (d) {
        if (d !== dialog && isTooltip(d) && d.dataset.popupHold !== 'true') close(d);
      });
    }
    // A mouse click leaves the trigger focused, but a scripted open may not —
    // fall back to the trigger so closing never drops focus to the top of the page.
    var here = document.activeElement;
    dialog.__returnTo =
      here && here !== document.body ? here : (dialog.__triggers && dialog.__triggers[0]) || null;
    dialog.__pinned = !opts.hover;
    dialog.__focused = false;

    if (tooltip) {
      dialog.__anchor = anchorFor(dialog, opts.anchor);
      dialog.__scrollY = window.scrollY;
      dialog.setAttribute('aria-modal', 'false');
      place(dialog);
    } else {
      dialog.setAttribute('aria-modal', 'true');
    }

    // The window opens from a smaller scale or an offset (the open animation's first frame), and a
    // slider inside measures its slides with getBoundingClientRect, which a scale shrinks: measured
    // then, the first slide lands off centre and only snaps into place once the window is full size.
    // So the window is held at full size for the frame in which anything inside re-measures, and the
    // animation starts from its usual first frame after that, before anything has been painted.
    var box = tooltip ? null : dialog.querySelector('.sugar-popup-dialog__container');
    var from = box ? getComputedStyle(box).transform : 'none';
    var hold = !!box && from !== 'none';
    if (hold) {
      box.style.transition = 'none';
      box.style.transform = 'none';
    }
    dialog.classList.add('is-open');
    // Anything measured while the dialog was hidden (a Splide slider, a clamp) re-measures now that
    // the dialog is on screen. Splide answers on the next frame, ahead of the release below.
    window.dispatchEvent(new Event('resize'));
    requestAnimationFrame(function () {
      if (hold) {
        box.style.transform = from;
        void box.offsetWidth;
        box.style.transition = '';
        box.style.transform = '';
      }
      dialog.dispatchEvent(new CustomEvent('sugar:popup:open', { bubbles: true }));
    });
    dialog.removeAttribute('aria-hidden');
    openStack.push(dialog);
    lockScroll();

    setExpanded(dialog, 'true');

    // A hover-opened tooltip must not steal focus from the text under the
    // pointer; the visitor is reading, not operating a dialog.
    if (tooltip && opts.hover) return;

    // focus() is a no-op while the dialog is still visibility:hidden, so flush
    // the style change the class just made before reaching for the first stop.
    void dialog.offsetHeight;

    var first = dialog.querySelector('[data-popup-close]') || focusables(dialog)[0];
    if (!first) {
      first = dialog.querySelector('[data-popup-container]');
      if (first) first.setAttribute('tabindex', '-1');
    }
    if (first) {
      first.focus({ preventScroll: true });
      dialog.__focused = true;
    }
  }

  function close(dialog) {
    cancelClose(dialog);
    if (dialog.dataset.popupHold === 'true') return;
    if (!dialog.classList.contains('is-open')) return;
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    openStack = openStack.filter(function (d) {
      return d !== dialog;
    });
    unlockScroll();
    var host = dialog.closest('.shopify-section');
    if (host && !host.querySelector('.sugar-popup-dialog.is-open')) host.classList.remove('sugar-popup-host-open');

    setExpanded(dialog, 'false');

    var back = dialog.__returnTo;
    if (dialog.__focused && back && document.contains(back) && typeof back.focus === 'function') {
      back.focus({ preventScroll: true });
    }
    dialog.__returnTo = null;
    dialog.__pinned = false;
    dialog.__focused = false;
  }

  function scheduleClose(dialog) {
    cancelClose(dialog);
    dialog.__closeTimer = setTimeout(function () {
      dialog.__closeTimer = null;
      if (!dialog.__pinned) close(dialog);
    }, HOVER_OUT);
  }

  function cancelClose(dialog) {
    if (dialog.__closeTimer) clearTimeout(dialog.__closeTimer);
    dialog.__closeTimer = null;
    if (dialog.__openTimer) clearTimeout(dialog.__openTimer);
    dialog.__openTimer = null;
  }

  function trapTab(e) {
    var dialog = openStack[openStack.length - 1];
    if (!dialog) return;
    // A tooltip the visitor never focused into is not holding the keyboard.
    if (isTooltip(dialog) && !dialog.__focused) return;
    var items = focusables(dialog);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var here = document.activeElement;

    if (!dialog.contains(here)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus({ preventScroll: true });
      return;
    }
    if (e.shiftKey && here === first) {
      e.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!e.shiftKey && here === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  /* ---- triggers ---------------------------------------------------------- */

  function activate(dialog, el) {
    if (!isTooltip(dialog)) {
      open(dialog, { anchor: el });
      return;
    }
    if (!dialog.classList.contains('is-open')) {
      open(dialog, { anchor: el });
    } else if (dialog.__pinned) {
      close(dialog);
    } else {
      dialog.__pinned = true;
      cancelClose(dialog);
    }
  }

  function bindTrigger(el, dialog) {
    if (el.__popupBound) return;
    el.__popupBound = true;
    dialog.__triggers = dialog.__triggers || [];
    dialog.__triggers.push(el);

    if (!el.hasAttribute('aria-haspopup')) el.setAttribute('aria-haspopup', 'dialog');
    el.setAttribute('aria-expanded', 'false');

    if (el.tagName !== 'BUTTON' && el.tagName !== 'A' && !el.hasAttribute('role')) {
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
    }

    el.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      activate(dialog, el);
    });

    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (el.tagName === 'BUTTON' || el.tagName === 'A') return;
      e.preventDefault();
      activate(dialog, el);
    });

    el.addEventListener('mouseenter', function () {
      if (!isTooltip(dialog) || !hoverQuery.matches) return;
      cancelClose(dialog);
      if (dialog.classList.contains('is-open')) return;
      dialog.__openTimer = setTimeout(function () {
        dialog.__openTimer = null;
        open(dialog, { anchor: el, hover: true });
      }, HOVER_IN);
    });

    el.addEventListener('mouseleave', function () {
      if (!isTooltip(dialog)) return;
      if (dialog.__openTimer) {
        clearTimeout(dialog.__openTimer);
        dialog.__openTimer = null;
      }
      if (!dialog.__pinned) scheduleClose(dialog);
    });
  }

  function placeIcon(el, dialog) {
    var tpl = dialog.__iconTemplate;
    if (!tpl || el.querySelector('.sugar-popup-trigger-icon')) return;
    var node = tpl.content.cloneNode(true);
    if (tpl.dataset.popupIconAt === 'before') {
      el.insertBefore(node, el.firstChild);
    } else {
      el.appendChild(node);
    }
  }

  function bindMarks(dialog) {
    var scope = dialog.__scope;
    var mark = dialog.dataset.popupMark;
    if (!scope || !mark) return;
    var id = dialog.dataset.popupDialog;
    // "The whole block, with a corner mark": the block is the trigger, so the mark is only a
    // hint in the image's bottom-right corner, never a second control inside it.
    var hint = dialog.hasAttribute('data-popup-mark-hint');
    Array.prototype.forEach.call(scope.querySelectorAll(mark), function (host) {
      if (host.closest('[data-popup-dialog]')) return;
      if (host.querySelector(':scope > .sugar-popup-mark[data-popup-open="' + id + '"]')) return;
      if (hint) {
        var tag = document.createElement('span');
        tag.className = 'sugar-popup-mark sugar-popup-mark--hint';
        tag.setAttribute('data-popup-open', id);
        tag.setAttribute('aria-hidden', 'true');
        if (dialog.__iconTemplate) tag.appendChild(dialog.__iconTemplate.content.cloneNode(true));
        host.appendChild(tag);
        return;
      }
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sugar-popup-mark';
      btn.setAttribute('data-popup-open', id);
      btn.setAttribute('aria-label', dialog.getAttribute('aria-label') || 'More information');
      if (dialog.__iconTemplate) btn.appendChild(dialog.__iconTemplate.content.cloneNode(true));
      host.appendChild(btn);
      bindTrigger(btn, dialog);
    });
  }

  function bindScope(dialog) {
    bindWithin(dialog);
    bindMarks(dialog);
  }

  function bindWithin(dialog) {
    var scope = dialog.__scope;
    var within = dialog.dataset.popupWithin;
    if (!scope || !within) return;
    var id = dialog.dataset.popupDialog;
    // Words a host rewrote are gone from the page; forget them before
    // binding the fresh ones so the trigger list never grows stale.
    dialog.__triggers = (dialog.__triggers || []).filter(function (el) {
      return document.contains(el);
    });
    Array.prototype.forEach.call(scope.querySelectorAll(within), function (el) {
      // Words inside another block's own popup content, or inside this
      // dialog, are not this dialog's trigger.
      if (el.closest('[data-popup-dialog]')) return;
      el.setAttribute('data-popup-open', id);
      placeIcon(el, dialog);
      bindTrigger(el, dialog);
    });
  }

  function watchScope(dialog) {
    var scope = dialog.__scope;
    if (!scope || dialog.__observer || typeof MutationObserver === 'undefined') return;
    var pending = false;
    // A timeout, not an animation frame: frames stop in a background tab and
    // the fresh words would sit unbound until it was shown again.
    dialog.__observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        bindScope(dialog);
      }, 0);
    });
    dialog.__observer.observe(scope, { childList: true, subtree: true });
  }

  function wire(dialog) {
    if (dialog.__popupWired) return;
    dialog.__popupWired = true;

    var id = dialog.dataset.popupDialog;
    // Both wirings search where the dialog was authored, so remember that
    // before it is moved to <body>.
    var origin = dialog.parentElement;
    var prevSibling = dialog.previousElementSibling;

    // The theme editor re-renders a block on every setting change, so a fresh
    // copy of this dialog arrives while the old one may still be open under
    // the merchant's eye. Retire the old copy and, if it was open, open the
    // new one in its place so the window never snaps shut mid-edit.
    var wasOpen = false;
    var stale = document.body.querySelector(':scope > [data-popup-dialog="' + id + '"]');
    if (stale && stale !== dialog) {
      if (stale.__observer) stale.__observer.disconnect();
      if (stale.classList.contains('is-open')) {
        wasOpen = true;
        openStack = openStack.filter(function (d) {
          return d !== stale;
        });
        unlockScroll();
      }
      stale.remove();
    }
    dialog.__iconTemplate = dialog.querySelector(':scope > template[data-popup-icon]');
    // The move to <body> (out of any transformed ancestor that would trap a
    // fixed box) waits until the dialog first opens: re-inserting it while
    // closed made the browser fetch every lazy image inside it at once.

    Array.prototype.forEach.call(document.querySelectorAll('[data-popup-open="' + id + '"]'), function (el) {
      bindTrigger(el, dialog);
    });

    var prev = dialog.dataset.popupPrev;
    if (prev && origin) {
      var sib = prevSibling;
      while (sib && !sib.matches(prev)) sib = sib.previousElementSibling;
      if (!sib) sib = origin.closest(prev);
      if (sib) {
        sib.setAttribute('data-popup-open', id);
        bindTrigger(sib, dialog);
      }
    }

    if ((dialog.dataset.popupWithin || dialog.dataset.popupMark) && origin) {
      var root = dialog.dataset.popupWithinRoot;
      dialog.__scope = (root && origin.closest(root)) || origin;
      bindScope(dialog);
      watchScope(dialog);
    }

    Array.prototype.forEach.call(dialog.querySelectorAll('[data-popup-close]'), function (btn) {
      btn.addEventListener('click', function () {
        close(dialog);
      });
    });

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog && dialog.dataset.popupOutside !== 'false') close(dialog);
    });

    // Moving the pointer from the words into the bubble keeps it open.
    dialog.addEventListener('mouseenter', function () {
      if (isTooltip(dialog)) cancelClose(dialog);
    });
    dialog.addEventListener('mouseleave', function () {
      if (isTooltip(dialog) && !dialog.__pinned) scheduleClose(dialog);
    });

    var test = dialog.dataset.popupTest;
    if (wasOpen && test !== 'animate') open(dialog);
    if (test === 'keep_open') {
      open(dialog);
      dialog.dataset.popupHold = 'true';
    } else if (test === 'animate') {
      var speed = parseInt(dialog.dataset.popupSpeed, 10) || 250;
      (function loop() {
        if (isTooltip(dialog)) {
          dialog.__anchor = anchorFor(dialog);
          place(dialog);
        }
        dialog.classList.add('is-open');
        setTimeout(function () {
          dialog.classList.remove('is-open');
          setTimeout(loop, speed + 400);
        }, 2000);
      })();
    }
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-popup-dialog]'), wire);
  }

  // A copy of a trigger keeps data-popup-open but loses the listener bindTrigger
  // gave the original, so catch those here. Bound elements are left to their own
  // handler; capture keeps the click off whatever the copy sits inside.
  function delegated(e) {
    var el = e.target.closest && e.target.closest('[data-popup-open]');
    if (!el) return anchored(e);
    if (el.__popupBound) return null;
    var dialog = document.querySelector('[data-popup-dialog="' + el.getAttribute('data-popup-open') + '"]');
    if (!dialog) return null;
    e.preventDefault();
    e.stopPropagation();
    dialog.__anchorHint = el;
    return dialog;
  }

  // A popup "opened from a link": any <a href="#anchor"> on the page whose hash matches a
  // popup's Anchor ID opens that popup instead of jumping.
  function anchored(e) {
    var link = e.target.closest && e.target.closest('a[href^="#"]');
    if (!link) return null;
    var hash = link.getAttribute('href').slice(1);
    if (!hash) return null;
    var host = document.querySelector('[data-popup-anchor="' + hash.replace(/"/g, '') + '"]');
    if (!host) return null;
    var dialog = document.querySelector('[data-popup-dialog="' + host.getAttribute('data-popup-anchor-for') + '"]');
    if (!dialog) return null;
    e.preventDefault();
    e.stopPropagation();
    dialog.__anchorHint = link;
    return dialog;
  }

  document.addEventListener(
    'click',
    function (e) {
      var dialog = delegated(e);
      if (dialog) activate(dialog, dialog.__anchorHint);
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var target = e.target;
      if (!target || !target.tagName) return;
      if (TYPEABLE.test(target.tagName) || target.isContentEditable) return;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') return;
      var dialog = delegated(e);
      if (dialog) activate(dialog, dialog.__anchorHint);
    },
    true
  );

  // A tooltip has no overlay to click, so a click anywhere outside it (and
  // outside the words that opened it) closes it.
  document.addEventListener('click', function (e) {
    if (!openStack.length) return;
    openStack.slice().forEach(function (dialog) {
      if (!isTooltip(dialog)) return;
      if (dialog.dataset.popupOutside === 'false') return;
      if (dialog.contains(e.target)) return;
      var trigger = e.target.closest && e.target.closest('[data-popup-open="' + dialog.dataset.popupDialog + '"]');
      if (trigger) return;
      close(dialog);
    });
  });

  document.addEventListener('keydown', function (e) {
    if (!openStack.length) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close(openStack[openStack.length - 1]);
    } else if (e.key === 'Tab') {
      trapTab(e);
    }
  });

  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);

  // Crossing the breakpoint swaps a tooltip for a sheet and back; anything open
  // at that moment is closed rather than left in the wrong shape.
  if (mobileQuery.addEventListener) {
    mobileQuery.addEventListener('change', function () {
      openStack.slice().forEach(function (d) {
        if (d.dataset.popupHold !== 'true') close(d);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  // Markup can arrive without a section:load — the theme editor swaps a single
  // block's markup on a setting change, and some hosts render at runtime — so
  // watch the page for dialogs that have not been wired yet. wire() is a no-op
  // on the ones that have, so a full pass is cheap.
  if (typeof MutationObserver !== 'undefined') {
    var scanPending = false;
    new MutationObserver(function (records) {
      if (scanPending) return;
      var added = false;
      for (var i = 0; i < records.length && !added; i++) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue;
          if (n.hasAttribute('data-popup-dialog') || n.querySelector('[data-popup-dialog]')) {
            added = true;
            break;
          }
        }
      }
      if (!added) return;
      scanPending = true;
      setTimeout(function () {
        scanPending = false;
        scan(document);
      }, 0);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // The dialog for a selected block is either the block itself or the one it
  // sits inside — and by then it has been moved to <body>, so it is looked up
  // by id rather than searched for under the block wrapper.
  function dialogFor(e) {
    var id = e.detail && e.detail.blockId;
    return (
      (e.target.closest && e.target.closest('[data-popup-dialog]')) ||
      (id && document.querySelector('[data-popup-dialog="' + id + '"]')) ||
      null
    );
  }

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var dialog = dialogFor(e);
    if (dialog) open(dialog);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var dialog = dialogFor(e);
    if (dialog) close(dialog);
  });

  window.SugarPopup = { open: open, close: close, scan: scan };
})();
